/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import CardWithTitle from 'components/atoms/CardWithTitle'
import React, {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import PurchaseHistoryDetailStyle from 'pages/ProductHistory/PurchaseHistoryDetail/style'
import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import useProductManagement from 'features/productManagement/useProductManagement'
import {
  PAYMENT_METHOD,
  PREPAYMENT_ITEM,
  TYPE_GOOD_TYPE,
  TYPE_INDICATOR_PRODUCT,
  UNIT_PRICES,
} from 'pages/ProductsPages/constant'
import {isoStringToDateString} from 'helpers/dateHelper'
import {useLocation, useParams} from 'react-router-dom'
import {
  fetchProductDetailUserApi,
  getPaymentDetailApi,
  PaymentInformationResponse,
  ProductUserDetailResponse,
} from 'api/productApi'
import {formatPriceNumber} from 'helpers/uiHelper'
import {usdToCent} from 'helpers/paymentHelper'
import {PACKAGE_AVAILABLE_IMAGE_ID} from 'pages/ProductOrderPages/constants'
import {useAppDispatch} from 'hooks'
import {
  openReportImageDialog,
  setReportDialogId,
} from 'features/modal/modalSlice'

const useStyles = makeStyles(PurchaseHistoryDetailStyle)

interface Params {
  id: string
}

const ProductHistoryDetail = () => {
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const {id} = useParams<Params>()
  const {
    listPackage,
    getPackageList,
    getNameProduct,
    getPeriodProduct,
    getQuantityLabel,
    getOrder,
  } = useProductManagement()

  const [detailProduct, setDetailProduct] =
    React.useState<ProductUserDetailResponse>()
  const [currentPayment, setCurrentPayment] =
    React.useState<PaymentInformationResponse>()
  const location = useLocation()

  const listHeader = [
    {
      id: 0,
      label: t('IDivision'),
    },
    {
      id: 1,
      label: t('IAnalysisIndicatorsV1'),
    },
    {
      id: 2,
      label: t('IPaidPoints'),
    },
  ]

  const getCLS = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    if (item.length) {
      return item[0].cls
    }
    return ''
  }

  const getPackageName = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    return (
      item[0]?.displayInfos?.find(
        (info) => info.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
      )?.name || ''
    )
  }

  const data = useMemo(() => {
    const value = detailProduct?.offer?.offerItems
      .map((item) => {
        return {
          ...item,
          cls: getCLS(item.pkgItemId),
          pkgName: getPackageName(item.pkgItemId),
          order: getOrder(item.pkgItemId),
        }
      })
      .toSorted((a, b) => a.cls.localeCompare(b.cls) || a.order - b.order)

    return value
  }, [detailProduct, i18n.language])

  const rowSpanMap = (data ?? []).reduce<Record<string, number>>(
    (acc, curr) => {
      acc[curr.cls] = (acc[curr.cls] || 0) + 1
      return acc
    },
    {},
  )
  const seenCls = new Set<string>()

  const dispatch = useAppDispatch()

  const getProductType = () => {
    if (detailProduct?.sale.paymentMethod === PAYMENT_METHOD.PREPAYMENT) {
      if (detailProduct.offer.offerType === PREPAYMENT_ITEM.PROVIDER) {
        return t('ISubscription')
      }
      if (detailProduct.offer.offerType === PREPAYMENT_ITEM.MONTHLY_OFFER) {
        return t('IPrepaid')
      }
    }
    return t('IManual')
  }

  const isCancellation = location.pathname.includes('cancellation')

  const getPaymentDetail = async () => {
    const res = await getPaymentDetailApi(id)
    setCurrentPayment(res.data)
  }

  const isChargedProduct =
    currentPayment?.order.orderItems[0].goodsType === TYPE_GOOD_TYPE.CHARGE

  const getItemQuantity = useMemo(() => {
    return (item: any) => {
      if (isChargedProduct) {
        const currentItem = currentPayment?.order.orderItems.find(
          (order) => order.chargeId === item.offerItemId,
        )
        if (currentItem) {
          return currentItem?.purchaseCount === 0
            ? '-'
            : `${currentItem?.purchaseCount || 0} ${
                item.cls === 'PBM' ? t('IMinute') : t('ICase')
              }`
        }
        return `0 ${item.cls === 'PBM' ? t('IMinute') : t('ICase')}`
      }
      return getQuantityLabel(item.offerAmount) !== t('IUnlimited')
        ? `${getQuantityLabel(item.offerAmount)} ${
            item.cls === 'PBM' ? t('IMinutes') : t('ICase')
          }`
        : t('IUnlimited')
    }
  }, [t, isChargedProduct, currentPayment])

  const productTypeLabel = getProductType()

  useEffect(() => {
    if (!listPackage?.length) {
      getPackageList({
        target: TYPE_INDICATOR_PRODUCT.ALL,
      })
    }
  }, [listPackage])

  useEffect(() => {
    const fetchGoodsName = async () => {
      try {
        const res = await fetchProductDetailUserApi({
          goodsId: currentPayment?.order.orderItems[0].goodsId || '',
        })
        setDetailProduct(res.data)
      } catch (error) {
        console.error(error)
      }
    }

    if (currentPayment?.order.orderItems[0].goodsId) {
      fetchGoodsName()
    }
  }, [currentPayment])

  const showReportImage = (packageId: string) => {
    if (PACKAGE_AVAILABLE_IMAGE_ID.includes(packageId)) {
      dispatch(setReportDialogId(packageId))
      dispatch(openReportImageDialog())
    }
  }

  useEffect(() => {
    getPaymentDetail()
  }, [])

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 4,
      }}
    >
      <CardWithTitle
        title={t('IPurchaseHistoryDetail')}
        style={{
          border: 'none',
        }}
      >
        <div className={classes.detailTitle}>
          {t(isCancellation ? 'ICancellationDetail' : 'IPurchaseDetail')}
        </div>
        <div className={classes.cardWrapper}>
          <div
            className={classes.informationWrapper}
            style={{
              gap: 20,
            }}
          >
            <div className={classes.cardTitle}>
              ■ {t('IProductInformation')}
            </div>
            <div className={`${classes.card}`}>
              <div className={classes.cardTitle}>
                {t('IProductName')}:{' '}
                {detailProduct
                  ? getNameProduct(detailProduct.goods.goodsName).name || ''
                  : ''}
              </div>

              <div className={classes.productSection}>
                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IAmount')}:{' '}
                    {currentPayment?.order.price.currency || 'KRW'}{' '}
                    {formatPriceNumber(
                      Number(
                        currentPayment?.order.price.currency === UNIT_PRICES.USD
                          ? usdToCent(currentPayment?.order.price.price || 0)
                          : currentPayment?.order.price.price || 0,
                      ),
                    )}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IType')}: {productTypeLabel}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IPeriod')}:{' '}
                    {detailProduct ? getPeriodProduct(detailProduct.offer) : ''}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IDescription')}:{' '}
                    {detailProduct
                      ? getNameProduct(detailProduct.goods.goodsName)
                          .description || ''
                      : ''}
                  </span>
                </div>
              </div>
              <Table
                size='small'
                className={classes.table}
                style={{tableLayout: 'fixed'}}
              >
                <TableHead className={classes.tableHeader}>
                  {listHeader.map((item) => (
                    <TableCell
                      align='center'
                      padding='checkbox'
                      key={item.id}
                      style={{
                        whiteSpace: 'pre',
                      }}
                      className={classes.headerMax}
                    >
                      {t(item.label)}
                    </TableCell>
                  ))}
                </TableHead>
                <TableBody>
                  {data?.map((item) => {
                    const isFirstOccurCls = !seenCls.has(item.cls)
                    if (isFirstOccurCls) seenCls.add(item.cls)
                    return (
                      <TableRow key={item.pkgItemId}>
                        {isFirstOccurCls && (
                          <TableCell
                            align='center'
                            padding='checkbox'
                            rowSpan={rowSpanMap[item.cls]}
                            className={classes.headerMax}
                          >
                            {item.cls}
                          </TableCell>
                        )}
                        <TableCell
                          align='center'
                          padding='checkbox'
                          className={classes.headerMax}
                        >
                          <div
                            className={
                              PACKAGE_AVAILABLE_IMAGE_ID.includes(
                                item.pkgItemId,
                              )
                                ? classes.clickableText
                                : ''
                            }
                            onClick={() => showReportImage(item.pkgItemId)}
                          >
                            {item.pkgName}
                          </div>
                        </TableCell>
                        <TableCell
                          align='center'
                          padding='checkbox'
                          className={classes.headerMax}
                        >
                          {getItemQuantity(item)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          {!isCancellation && (
            <div className={classes.informationWrapper}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div className={classes.cardTitle}>
                  ■ {t('IPaymentInformation')}
                </div>
                {currentPayment?.payment.receiptUrl && (
                  <Button
                    color='primary'
                    variant='contained'
                    onClick={() => {
                      window.open(
                        `${currentPayment?.payment.receiptUrl}`,
                        '_blank',
                      )
                    }}
                  >
                    {t('IReceipt')}
                  </Button>
                )}
              </div>
              <div className={`${classes.card}`}>
                <div className={classes.productSection}>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IOrderNumber')}: {currentPayment?.payment.paymentNo}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPurchaseAmount')}:{' '}
                      {currentPayment?.order.price.currency || 'KRW'}{' '}
                      {formatPriceNumber(
                        Number(
                          currentPayment?.order.price.currency ===
                            UNIT_PRICES.USD
                            ? usdToCent(currentPayment?.order.price.price || 0)
                            : currentPayment?.order.price.price || 0,
                        ),
                      )}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPaidDate')}:{' '}
                      {isoStringToDateString(
                        String(currentPayment?.order.orderAt || ''),
                      )}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPaymentMethod')}:{' '}
                      {`${currentPayment?.payment.paymentType} / ${productTypeLabel}`}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IFirstName')}: {currentPayment?.payment.customer.name}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPhone')}:{' '}
                      {currentPayment?.payment.customer.phoneNumber}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IEmail')}: {currentPayment?.payment.customer.email}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {isCancellation && currentPayment && currentPayment.cancellations && (
            <div className={classes.informationWrapper}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div className={classes.cardTitle}>
                  ■ {t('IPaymentInformation')}
                </div>
                {currentPayment?.cancellations[0].receiptUrl && (
                  <Button
                    color='primary'
                    variant='contained'
                    onClick={() => {
                      window.open(
                        `${currentPayment?.cancellations[0].receiptUrl}`,
                        '_blank',
                      )
                    }}
                  >
                    {t('IReceipt')}
                  </Button>
                )}
              </div>
              <div className={`${classes.card}`}>
                <div className={classes.productSection}>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IOrderNumber')}: {currentPayment?.payment.paymentNo}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('ICancelAmount')}:{' '}
                      {currentPayment?.cancellations[0].price.currency || 'KRW'}{' '}
                      {formatPriceNumber(
                        Number(
                          currentPayment?.cancellations[0].price.currency ===
                            UNIT_PRICES.USD
                            ? usdToCent(
                                currentPayment?.cancellations[0].price.price ||
                                  0,
                              )
                            : currentPayment?.cancellations[0].price.price || 0,
                        ),
                      )}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('ICancellationDate')}:{' '}
                      {isoStringToDateString(
                        new Date(
                          currentPayment.cancellations[0].canceledAt || 0,
                        ).toISOString(),
                      )}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('ICancellationMethod')}:{' '}
                      {`${currentPayment?.cancellations[0].paymentType} / ${productTypeLabel}`}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IFirstName')}:{' '}
                      {`${currentPayment?.payment.customer.name}`}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPhone')}:{' '}
                      {currentPayment?.payment.customer.phoneNumber}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IEmail')}: {currentPayment?.payment.customer.email}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardWithTitle>
    </div>
  )
}

export default ProductHistoryDetail
